import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

// status defaults to OPEN on the backend; pass 'ALL' to see resolved rows too.
export function useBackorders(status: string = 'OPEN') {
  return useQuery({
    queryKey: ['backorders', status] as const,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/backorders', {
        params: { query: { status } },
      })
      if (error) throw new Error('Failed to load backorders')
      return data
    },
  })
}

export function useUpdateBackorder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: number; qty?: number; notes?: string }) => {
      const { data, error } = await apiClient.PATCH('/api/backorders/{backorder_id}', {
        params: { path: { backorder_id: vars.id } },
        body: { qty: vars.qty, notes: vars.notes },
      })
      if (error) throw new Error('Failed to update backorder')
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backorders'] }),
  })
}

export function useResolveBackorder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data, error } = await apiClient.POST('/api/backorders/{backorder_id}/resolve', {
        params: { path: { backorder_id: id } },
      })
      if (error) throw new Error('Failed to resolve backorder')
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backorders'] }),
  })
}

// Move a placed-PO line item to the backorder / OOS list (removes it from the PO).
export function useBackorderPOItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { poId: string; poItemId: number }) => {
      const { data, error } = await apiClient.POST(
        '/api/purchase-orders/{po_id}/items/{po_item_id}/backorder',
        { params: { path: { po_id: vars.poId, po_item_id: vars.poItemId } } }
      )
      if (error) {
        const detail = (error as { detail?: string })?.detail ?? 'Failed to move item to backorder'
        throw new Error(detail)
      }
      return data
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['backorders'] })
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po', vars.poId] })
    },
  })
}
